import React, { useState, useEffect, useMemo } from 'react';
import { Luna, ErrorLuna } from './Luna';
import { usei18n } from "@/components/context/i18nContext";
import styles from './styles/StepProject.module.css';
import inputStyles from './styles/OnboardingInput.module.css';
import layoutStyles from './styles/Layout.module.css';
import sharedStyles from './styles/BrandSignup.module.css';


const StepProject = ({ brandSignup, categories, onComplete, onSkipToDashboard, loading, error, onBack, canGoBack }) => {
  const { t } = usei18n();
  const translations = t.brandSignup.stepProject;

  const [formData, setFormData] = useState({
    rfq_name: brandSignup.rfq_name || '',
    parent_category: brandSignup.parent_category || '',
    urgency: brandSignup.urgency || ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [open, setOpen] = useState(false);
  const [aiStatus, setAiStatus] = useState(brandSignup.ai_enrichment_status || 'processing');
  const [pollCount, setPollCount] = useState(0);

  const hasErrors = () => {
    return Object.keys(formErrors).length > 0;
  };

  useEffect(() => {
    const isComplete =
      formData.rfq_name.trim().length > 0 &&
      formData.parent_category.length > 0 &&
      formData.urgency.length > 0;

    if (isComplete && !hasErrors()) {
      setEnableSubmit(true);
    } else {
      setEnableSubmit(false);
    }
  }, [formData, formErrors]);

  // Poll for AI enrichment status to auto-fill fields when ready
  useEffect(() => {
    // Only poll if AI is still processing
    if (aiStatus === 'completed' || aiStatus === 'failed') {
      return;
    }

    // Only poll if fields are empty (already filled means no need to poll)
    if (formData.rfq_name.trim() !== '' && formData.parent_category !== '') {
      return;
    }

    let pollInterval;
    const maxPolls = 20; // Poll for up to 1 minute (20 * 3 seconds)

    const pollAiStatus = async () => {
      if (pollCount >= maxPolls) {
        console.log('AI polling timeout - stopping after 20 attempts');
        setAiStatus('failed');
        if (pollInterval) {
          clearInterval(pollInterval);
        }
        return;
      }

      setPollCount(prev => prev + 1);

      try {
        const response = await fetch(`/brand_signup/${brandSignup.id}/enrichment_status`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          }
        });

        if (response.ok) {
          const data = await response.json();

          // Update status
          if (data.status !== aiStatus) {
            setAiStatus(data.status);
          }

          // Update form fields if AI provided suggestions and fields are still empty
          if (data.status === 'completed') {
            const updates = {};
            if (!formData.rfq_name.trim() && data.rfq_name) {
              updates.rfq_name = data.rfq_name;
            }
            if (!formData.parent_category && data.parent_category) {
              updates.parent_category = data.parent_category;
            }

            if (Object.keys(updates).length > 0) {
              setFormData(prev => ({ ...prev, ...updates }));
            }

            // Stop polling when completed
            if (pollInterval) {
              clearInterval(pollInterval);
            }
          }

          // Stop polling if failed
          if (data.status === 'failed') {
            if (pollInterval) {
              clearInterval(pollInterval);
            }
          }
        }
      } catch (error) {
        console.error('Error polling AI status:', error);
      }
    };

    // Start polling every 3 seconds
    pollInterval = setInterval(pollAiStatus, 3000);

    // Initial poll
    pollAiStatus();

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [brandSignup.id, aiStatus, pollCount, formData.rfq_name, formData.parent_category]);


  const validateForm = () => {
    const errors = {};

    if (!formData.rfq_name.trim()) {
      errors.rfq_name = 'Project name is required';
    }

    if (!formData.parent_category) {
      errors.parent_category = 'Please select a category';
    }

    if (!formData.urgency) {
      errors.urgency = 'Please select your urgency';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onComplete(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user makes a selection
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };


  const handleSelect = (category) => {
    handleInputChange('parent_category', category);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close only if clicking outside the dropdown container
      if (open && !event.target.closest(`.${styles.dropdownContainer}`)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const interpolate = (template, params) => {
    return template.replace(/\${(\w+)}/g, (match, key) => params[key] || match);
  };

  // Memoize Luna messages to prevent re-typing on form changes
  const lunaMessages = useMemo(() => {
    const firstName = brandSignup.first_name || 'there';
    return [`Ok, ${firstName}, now let's get back to building your product.`];
  }, [brandSignup.first_name]);

  // Memoize Luna components to prevent re-renders on form state changes
  const memoizedErrorLuna = useMemo(() =>
    <ErrorLuna lunaDialogs={lunaMessages} />,
    [lunaMessages]
  );

  const memoizedLuna = useMemo(() =>
    <Luna lunaDialogs={lunaMessages} />,
    [lunaMessages]
  );

  return (
    <div className={layoutStyles.stepContainer}>

      {Object.keys(formErrors).length > 0 ? memoizedErrorLuna : memoizedLuna}

      <form onSubmit={handleSubmit} className={layoutStyles.form}>
        <label className={styles.label}>{translations.projectNameLabel}</label>
        <input
          className={`${inputStyles.input} ${formErrors.rfq_name ? `${inputStyles.error}` : ''}`}
          type="text"
          placeholder={translations.projectNamePlaceholder}
          value={formData.rfq_name}
          onChange={(e) => handleInputChange('rfq_name', e.target.value)}
          disabled={loading}
        />

        <div className={styles.dropdownContainer}>
          <label className={styles.label}>{translations.categoryLabel}</label>
          <div
            className={`${styles.dropdownInput} ${formErrors.parent_category ? `${styles.error}` : ''}`}
            onClick={() => !loading && setOpen(!open)}
          >
            <span>
              {formData.parent_category || translations.selectCategory}
            </span>
            <i className={`ph ${open ? "ph-caret-up" : "ph-caret-down"} ${styles.customArrow}`} />
          </div>

          {open && (
            <ul className={styles.dropdownMenu}>
              {categories.map((category) => (
                <li
                  key={category}
                  className={styles.dropdownItem}
                  onClick={() => handleSelect(category)}
                >
                  {category}
                </li>
              ))}
            </ul>
          )}
        </div>



        <label className={styles.urgencyLabel}>{translations.urgency}</label>
        <div className={styles.urgencyContainer}>
          {
            translations.urgencyOptions.map((option) => (
              <label key={option.key} className={styles.urgencyOption}>
                <span className="">{option.label}</span>
                <input
                  type="radio"
                  name="urgency"
                  value={option.key}
                  checked={formData.urgency === option.key}
                  onChange={(e) => handleInputChange('urgency', e.target.value)}
                  disabled={loading}
                />

              </label>
            ))
          }
        </div>
        <div className={styles.buttonContainer}>
          <button
            type="submit"
            className={`${styles.button} ${enableSubmit ? '' : styles.buttonDisabled}`}
            disabled={loading || !enableSubmit}
          >
            Continue
          </button>
        </div>

        {canGoBack && (
          <a
            className={sharedStyles.backLink}
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            ← Back
          </a>
        )}

        {onSkipToDashboard && (
          <button
            type="button"
            className={sharedStyles.skipLink}
            onClick={onSkipToDashboard}
            disabled={loading}
          >
            I'll set up my project later. Take me to the Dashboard
          </button>
        )}

      </form>
    </div>
  );
};

export default StepProject;
